import React from "react";
import { Navigate } from "react-router-dom";

import {
  BillingRedirect,
  BillingReportsWithLayout,
  BillingSettingsWithLayout,
  ManualPaymentsWithLayout,
  SubscriptionsWithLayout,
  DiscountCodesWithLayout,
} from "./billingRouteWrappers";

// Import route constants and role groups
import { ROUTES } from "../constants/routes";
import { ROLE_GROUPS } from "../constants/roles";

// Import page components
import AccountManager from "../pages/AccountManager/AccountManager";
import DamageWaiversPage from "../pages/DamageWaivers/DamageWaivers";
import TemplateFieldsPage from "../pages/DamageWaivers/TemplateFields";
import UploadWaiverPage from "../pages/DamageWaivers/UploadWaiver";
import WaiverCompliancePage from "../pages/DamageWaivers/WaiverCompliance";
import SignupQRPage from "../pages/QRCode/SignupQR";
import Dashboard from "../pages/Dashboard/Dashboard";
import DeleteAccountPage from "../pages/Delete Account/DeleteAccount";
import CompanySearchPortal from "../pages/Developer/CompanySearchPortal";
import ImpersonationPortal from "../pages/Developer/ImpersonationPortal";
import GetInfo from "../pages/Get Info/GetInfo";
import PrivacyPolicy from "../pages/Privacy/Privacy";
import SecurityPolicy from "../pages/SecurityPolicy/SecurityPolicy";
import SmsConsentLegal from "../pages/SmsConsent/SmsConsentLegal";
import ResetPassword from "../pages/ResetPassword/ResetPassword";
import Signin from "../pages/Signin/Signin";
import DraftInviteAuthRedirect from "../pages/Signup/DraftInviteAuthRedirect";
import DraftInviteSuccess from "../pages/Signup/DraftInviteSuccess";
import DraftStudentInviteRedirect from "../pages/Signup/DraftStudentInviteRedirect";
import EnterInviteCode from "../pages/Signup/EnterInviteCode";
import InviteCodeSignup from "../pages/Signup/InviteCodeSignup";
import SmsConsent from "../pages/Signup/SmsConsent";
import VerifyOtp from "../pages/Signup/VerifyOtp";
import Signup from "../pages/Signup/Signup";
import StudentInviteRedirect from "../pages/Signup/StudentInviteRedirect";
import LocationInviteRedirect from "../pages/Signup/LocationInviteRedirect";
import AcceptCompanyInvitation from "../pages/CompanyInvitation/AcceptCompanyInvitation";
import OwnerSignup from "../pages/OwnerSignup/OwnerSignup";
import Onboarding from "../pages/Onboarding/Onboarding";
import BillingOnboarding from "../pages/Onboarding/Billing/BillingOnboarding";
import StudentOnboarding from "../pages/Onboarding/StudentOnboarding";
import GuidedStudentOnboarding from "../pages/Onboarding/GuidedStudentOnboarding";
import StudentHome from "../pages/StudentHome/StudentHome";
import StudentMessagesPage from "../pages/StudentMessages/StudentMessages";
import StudentJournalPage from "../pages/StudentJournal/StudentJournal";
import CreateGym from "../pages/CreateGym/CreateGym";
import SubscriptionsPlansIndexPage from "../pages/SubscriptionsPlans/index";
import RollOpsSubscriptionPage from "../pages/SubscriptionsPlans/RollOpsSubscription";
import FeedPage from "../pages/Feed/Feed";
import AccountSettingsPage from "../pages/Settings/AccountSettings";
import BeltProfilesList from "../pages/Belts/BeltProfilesList";
import BeltProfileEditor from "../pages/Belts/BeltProfileEditor";
import StudentRanksPage from "../pages/Belts/StudentRanksPage";
import BeltPromotionsPage from "../pages/Belts/BeltPromotionsPage";
import BeltHistoryPage from "../pages/Belts/BeltHistoryPage";
import MyRankPage from "../pages/Belts/MyRankPage";
import SocialDashboard from "../pages/Social/SocialDashboard";
import SocialModeration from "../pages/Social/SocialModeration";
import SocialAnalytics from "../pages/Social/SocialAnalytics";
import SchedulePage from "../pages/Schedule/Schedule";
import TimeCard from "../pages/TimeCard/TimeCard";
import ScheduleSettingsPage from "../pages/Schedule/ScheduleSettings";
import NotesPage from "../pages/Notes/Notes";
import ActivityPage from "../pages/Activity/Activity";
import PaymentsPage from "../pages/Payments/Payments";
import WebHostingInterestPage from "../pages/WebHostingInterest/WebHostingInterest";
import UploadStudentData from "../pages/UploadStudentData/UploadStudentData";
import BillingSettingsRedirect from "../pages/BillingSettingsRedirect";
import BillingUnified from "../pages/Billing/BillingUnified";
import BillingPayments from "../pages/Billing/BillingPayments";
import CompanySelectorPage from "../pages/CompanySelector/CompanySelector";
import Join from "../pages/Join/Join";
import BackendDashboard from "../pages/Admin/BackendDashboard";
import CustomerDashboard from "../pages/Admin/CustomerDashboard";
import SurveyBuilderPage from "../pages/Admin/SurveyBuilder";
import SupportRequests from "../pages/Admin/SupportRequests";
import StripeManagementPage from "../pages/Admin/StripeManagement";
import RollOpsSubscriptionsPage from "../pages/Admin/RollOpsSubscriptions";
import DefaultWaiverUploaderPage from "../pages/Admin/DefaultWaiverUploader";
import SubscriptionManagementPage from "../pages/Admin/SubscriptionManagement";
import AdminFeeSchedulesPage from "../pages/Admin/AdminFeeSchedules";
import FeeScheduleDetailsPage from "../pages/Admin/FeeScheduleDetails";
import AdminJobs from "../pages/Admin/AdminJobs";
import AdminManagement from "../pages/Admin/AdminManagement";
import AdminQrGenerator from "../pages/Admin/AdminQrGenerator";
import Landing from "../pages/Landing/Landing";
import Terms from "../pages/Terms/Terms";
import MemberManagement from "../pages/Features/MemberManagement";
import BillingFeature from "../pages/Features/Billing";
import SchedulingFeature from "../pages/Features/Scheduling";
import FoundersPromo from "../pages/FoundersPromo/FoundersPromo";
import Prototype from "../pages/Prototype/Prototype";
import InventoryDashboard from "../pages/Inventory/InventoryDashboard";
import ProductsList from "../pages/Inventory/ProductsList";
import VariantDetail from "../pages/Inventory/VariantDetail";

// Import types
import type { RouteConfig } from "./types";

/**
 * Complete route configuration for the application
 * This replaces the scattered Route definitions in App.tsx
 */
export const routeDefinitions: RouteConfig[] = [
  // ============================================================================
  // DEEP-LINK ROUTES (bypass Layout to avoid redirects)
  // ============================================================================

  {
    path: ROUTES.RESET_PASSWORD,
    component: ResetPassword,
    layout: "none",
    seo: { title: "Reset Password", noindex: true },
  },
  {
    path: ROUTES.JOIN,
    component: Join,
    layout: "none",
    seo: { title: "Join RollOps", noindex: true },
  },
  {
    path: ROUTES.DRAFT_INVITE_WITH_TOKEN,
    component: DraftStudentInviteRedirect,
    layout: "none",
    seo: { title: "Draft Invite", noindex: true },
  },
  {
    path: ROUTES.DRAFT_INVITE,
    component: DraftStudentInviteRedirect,
    layout: "none",
    seo: { title: "Draft Invite", noindex: true },
  },
  {
    path: ROUTES.DRAFT_INVITE_AUTH,
    component: DraftInviteAuthRedirect,
    layout: "none",
    seo: { title: "Draft Invite Auth", noindex: true },
  },
  {
    path: ROUTES.ENTER_INVITE_CODE,
    component: EnterInviteCode,
    layout: "none",
    seo: { title: "Enter Invite Code", noindex: true },
  },
  {
    path: ROUTES.SIGNUP_INVITE_CODE,
    component: InviteCodeSignup,
    layout: "none",
    seo: { title: "Signup with Invite Code", noindex: true },
  },
  {
    path: ROUTES.SMS_CONSENT,
    component: SmsConsent,
    layout: "none",
    seo: { title: "SMS Consent", noindex: true },
  },
  {
    path: ROUTES.VERIFY_OTP,
    component: VerifyOtp,
    layout: "none",
    seo: { title: "Verify OTP", noindex: true },
  },
  {
    path: ROUTES.DRAFT_INVITE_SUCCESS,
    component: DraftInviteSuccess,
    layout: "none",
    seo: { title: "Draft Invite Success", noindex: true },
  },
  {
    path: ROUTES.COMPANY_INVITE_SUCCESS,
    component: DraftInviteSuccess,
    layout: "none",
    seo: { title: "Company Invite Success", noindex: true },
  },
  {
    path: ROUTES.STUDENT_LANDING,
    component: StudentHome,
    layout: "authenticated-student",
    seo: { title: "Student Home", noindex: true },
  },
  {
    path: ROUTES.STUDENT_SCHEDULE,
    component: SchedulePage,
    layout: "authenticated-student",
    seo: { title: "Schedule", noindex: true },
  },
  {
    path: ROUTES.STUDENT_MESSAGES,
    component: StudentMessagesPage,
    layout: "authenticated-student",
    seo: { title: "Messages", noindex: true },
  },
  {
    path: ROUTES.STUDENT_JOURNAL,
    component: StudentJournalPage,
    layout: "authenticated-student",
    seo: { title: "Journal", noindex: true },
  },
  {
    path: ROUTES.LOCATION_INVITE,
    component: LocationInviteRedirect,
    layout: "none",
    seo: { title: "Location Invite", noindex: true },
  },
  {
    path: ROUTES.STUDENT_INVITE,
    component: StudentInviteRedirect,
    layout: "none",
    seo: { title: "Student Invite", noindex: true },
  },
  {
    path: ROUTES.ACCEPT_COMPANY_INVITATION,
    component: AcceptCompanyInvitation,
    layout: "none",
    seo: { title: "Accept Company Invitation", noindex: true },
  },

  // ============================================================================
  // DEVELOPMENT/PROTOTYPE ROUTES
  // ============================================================================

  {
    path: "/prototype",
    component: Prototype,
    layout: "none",
    seo: { title: "Prototype Sandbox", noindex: true },
  },

  // ============================================================================
  // ADMIN/BACKEND ROUTES (admin layout)
  // ============================================================================

  {
    path: ROUTES.ADMIN_DASHBOARD,
    component: BackendDashboard,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Admin Dashboard", noindex: true },
  },
  {
    path: ROUTES.DEV_DASHBOARD,
    component: BackendDashboard,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Developer Dashboard", noindex: true },
  },
  {
    path: ROUTES.DEV_COMPANIES,
    component: CompanySearchPortal,
    layout: "admin",
    layoutProps: { skipTopPadding: true },
    guardType: "backendOnly",
    seo: { title: "Company Search Portal", noindex: true },
  },
  {
    path: ROUTES.DEV_IMPERSONATE,
    component: ImpersonationPortal,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Impersonation Portal", noindex: true },
  },
  {
    path: ROUTES.ADMIN_BACKEND_TOOLS,
    component: BackendDashboard,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Backend Tools", noindex: true },
  },
  {
    path: ROUTES.ADMIN_CUSTOMERS,
    component: CustomerDashboard,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Customer Dashboard", noindex: true },
  },
  {
    path: ROUTES.ADMIN_SURVEY_BUILDER,
    component: SurveyBuilderPage,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Survey Builder", noindex: true },
  },
  {
    path: ROUTES.ADMIN_SUPPORT_REQUESTS,
    component: SupportRequests,
    layout: "admin",
    guardType: "backendOnly",
    seo: { title: "Support Requests", noindex: true },
  },
  {
    path: ROUTES.ADMIN_STRIPE_MANAGEMENT,
    component: StripeManagementPage,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Stripe Management", noindex: true },
  },
  {
    path: ROUTES.ADMIN_ROLLOPS_SUBSCRIPTIONS,
    component: RollOpsSubscriptionsPage,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "RollOps Subscriptions", noindex: true },
  },
  {
    path: ROUTES.ADMIN_SUBSCRIPTION_MANAGEMENT,
    component: SubscriptionManagementPage,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Subscription Management", noindex: true },
  },
  {
    path: ROUTES.ADMIN_DEFAULT_WAIVER,
    component: DefaultWaiverUploaderPage,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Default Waiver Uploader", noindex: true },
  },
  {
    path: ROUTES.ADMIN_FEE_SCHEDULE_DETAILS,
    component: FeeScheduleDetailsPage,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Fee Schedule Details", noindex: true },
  },
  {
    path: ROUTES.ADMIN_FEE_SCHEDULES,
    component: AdminFeeSchedulesPage,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Fee Schedules", noindex: true },
  },
  {
    path: ROUTES.ADMIN_JOBS,
    component: AdminJobs,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Job Board", noindex: true },
  },
  {
    path: ROUTES.ADMIN_MANAGEMENT,
    component: AdminManagement,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "Admin Management", noindex: true },
  },
  {
    path: ROUTES.ADMIN_QR_GENERATOR,
    component: AdminQrGenerator,
    layout: "admin",
    guardType: "adminOnly",
    seo: { title: "QR Generator", noindex: true },
  },

  // ============================================================================
  // AUTHENTICATED DASHBOARD ROUTES (sidebar layout)
  // ============================================================================

  {
    path: ROUTES.DASHBOARD,
    component: Dashboard,
    layout: "authenticated-v2",
    guardType: "studentRedirect",
    seo: { title: "Dashboard", noindex: true },
  },
  {
    path: ROUTES.ACTIVITY,
    component: ActivityPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Activity", noindex: true },
  },
  {
    path: ROUTES.PAYMENTS,
    component: PaymentsPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Payments", noindex: true },
  },
  {
    path: ROUTES.WEB_HOSTING_INTEREST,
    component: WebHostingInterestPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Web Hosting Interest", noindex: true },
  },
  {
    path: ROUTES.STUDENTS,
    component: AccountManager,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Students", noindex: true },
  },
  {
    path: ROUTES.UPLOAD_STUDENT_DATA,
    component: UploadStudentData,
    layout: "authenticated-v2",
    guardType: "studentRedirect",
    seo: { title: "Upload Student Data", noindex: true },
  },
  {
    path: ROUTES.DAMAGE_WAIVERS,
    component: DamageWaiversPage,
    layout: "authenticated-v2",
    guardType: "studentRedirect",
    seo: { title: "Damage Waivers", noindex: true },
  },
  {
    path: ROUTES.DAMAGE_WAIVERS_UPLOAD,
    component: UploadWaiverPage,
    layout: "authenticated-v2",
    guardType: "studentRedirect",
    seo: { title: "Upload Waiver", noindex: true },
  },
  {
    path: ROUTES.DAMAGE_WAIVERS_TEMPLATE_FIELDS,
    component: TemplateFieldsPage,
    layout: "authenticated-v2",
    guardType: "studentRedirect",
    seo: { title: "Waiver Template Fields", noindex: true },
  },
  {
    path: ROUTES.DAMAGE_WAIVERS_COMPLIANCE,
    component: WaiverCompliancePage,
    layout: "authenticated-v2",
    guardType: "studentRedirect",
    seo: { title: "Waiver Compliance", noindex: true },
  },
  {
    path: ROUTES.QR_CODES,
    component: SignupQRPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "QR Codes", noindex: true },
  },
  {
    path: ROUTES.BILLING,
    component: BillingRedirect,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Billing", noindex: true },
  },
  {
    path: ROUTES.BILLING_UNIFIED,
    component: BillingUnified,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Billing & Subscriptions", noindex: true },
  },
  {
    path: ROUTES.BILLING_PAYMENTS,
    component: BillingPayments,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Payment History", noindex: true },
  },
  {
    path: ROUTES.BILLING_REPORTS,
    component: BillingReportsWithLayout,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Billing Reports", noindex: true },
  },
  {
    path: ROUTES.BILLING_SETTINGS,
    component: BillingSettingsWithLayout,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.OWNERS,
    seo: { title: "Billing Settings", noindex: true },
  },
  {
    path: ROUTES.INVENTORY_DASHBOARD,
    component: InventoryDashboard,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.OWNERS,
    seo: { title: "Inventory & Sales", noindex: true },
  },
  {
    path: ROUTES.INVENTORY_PRODUCTS,
    component: ProductsList,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.OWNERS,
    seo: { title: "Products & Variants", noindex: true },
  },
  {
    path: ROUTES.INVENTORY_VARIANT_DETAIL,
    component: VariantDetail,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.OWNERS,
    seo: { title: "Variant Detail", noindex: true },
  },
  {
    path: ROUTES.BILLING_MANUAL_PAYMENTS,
    component: ManualPaymentsWithLayout,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Manual Payments", noindex: true },
  },
  {
    path: ROUTES.SUBSCRIPTIONS_PLANS,
    component: SubscriptionsPlansIndexPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Subscription Plans", noindex: true },
  },
  {
    path: ROUTES.SUBSCRIPTIONS_PLANS_ROLLOPS,
    component: RollOpsSubscriptionPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "RollOps Subscription", noindex: true },
  },
  {
    path: ROUTES.SUBSCRIPTIONS_PLANS_GYM,
    component: SubscriptionsWithLayout,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Gym Subscriptions", noindex: true },
  },
  {
    path: ROUTES.BILLING_DISCOUNT_CODES,
    component: DiscountCodesWithLayout,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Discount Codes", noindex: true },
  },
  {
    path: ROUTES.DISCOUNT_CODES,
    component: () =>
      React.createElement(Navigate, {
        to: ROUTES.BILLING_DISCOUNT_CODES,
        replace: true,
      }),
    layout: "none",
  },
  {
    path: ROUTES.ACCOUNT_SETTINGS,
    component: AccountSettingsPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ALL,
    seo: { title: "Account Settings", noindex: true },
  },
  {
    path: ROUTES.BELTS,
    component: BeltProfilesList,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.OWNERS,
    seo: { title: "Belts & Ranks", noindex: true },
  },
  {
    path: ROUTES.BELT_PROFILE_EDIT,
    component: BeltProfileEditor,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.OWNERS,
    seo: { title: "Edit Belt Profile", noindex: true },
  },
  {
    // Owner + coach can manage student rank lifecycle. RPC double-checks the
    // role; STAFF here keeps reception out of the page but the RPC is the
    // real enforcement point.
    path: ROUTES.STUDENT_BELT_RANKS,
    component: StudentRanksPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Student Ranks", noindex: true },
  },
  {
    // Same guard as student rank lifecycle — owner/coach via the RPC.
    path: ROUTES.BELT_PROMOTIONS,
    component: BeltPromotionsPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Promotions", noindex: true },
  },
  {
    // Owner-facing promotion history + rank distribution (#750). Owners/admins
    // only — staff don't need the gym-wide view.
    path: ROUTES.BELT_HISTORY,
    component: BeltHistoryPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Belt History", noindex: true },
  },
  {
    // Student-facing rank progress (#750). Anyone with a company relationship
    // can view their OWN rank; useMyUserId scopes the query to auth.uid().
    path: ROUTES.MY_RANK,
    component: MyRankPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ALL,
    seo: { title: "My Rank", noindex: true },
  },
  {
    path: ROUTES.SOCIAL,
    component: SocialDashboard,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Social Dashboard", noindex: true },
  },
  {
    path: ROUTES.SOCIAL_MODERATION,
    component: SocialModeration,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Social Moderation", noindex: true },
  },
  {
    path: ROUTES.SOCIAL_ANALYTICS,
    component: SocialAnalytics,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Social Analytics", noindex: true },
  },
  {
    path: ROUTES.FEED,
    component: FeedPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Feed", noindex: true },
  },
  {
    path: ROUTES.SCHEDULE,
    component: SchedulePage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Schedule", noindex: true },
  },
  {
    path: ROUTES.TIME_CARD,
    component: TimeCard,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Time Card", noindex: true },
  },
  {
    path: ROUTES.SCHEDULE_SETTINGS,
    component: ScheduleSettingsPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.ADMINS,
    seo: { title: "Schedule Settings", noindex: true },
  },
  {
    path: ROUTES.NOTES,
    component: NotesPage,
    layout: "authenticated-v2",
    guardType: "companyRole",
    allowedRoles: ROLE_GROUPS.STAFF,
    seo: { title: "Notes", noindex: true },
  },

  // ============================================================================
  // LEGACY REDIRECTS
  // ============================================================================

  {
    path: ROUTES.SETTINGS_BILLING,
    component: () => React.createElement(BillingSettingsRedirect),
    layout: "none",
  },
  {
    path: ROUTES.BILLING_SETTINGS_REDIRECT,
    component: () => React.createElement(BillingSettingsRedirect),
    layout: "none",
  },
  {
    path: ROUTES.SUBSCRIPTIONS,
    component: () =>
      React.createElement(Navigate, {
        to: ROUTES.SUBSCRIPTIONS_PLANS_GYM,
        replace: true,
      }),
    layout: "none",
  },

  // ============================================================================
  // STANDALONE PAGES (no layout)
  // ============================================================================

  {
    path: "/owner-signup",
    component: OwnerSignup,
    layout: "none",
    seo: { title: "Owner Signup", noindex: true },
  },
  {
    path: ROUTES.CREATE_GYM,
    component: CreateGym,
    layout: "none",
    seo: { title: "Create Gym", noindex: true },
  },
  {
    path: ROUTES.ONBOARDING,
    component: Onboarding,
    layout: "none",
    seo: { title: "Onboarding", noindex: true },
  },
  {
    path: ROUTES.ONBOARDING_LOCATIONS,
    component: () =>
      React.createElement(Navigate, { to: ROUTES.ONBOARDING, replace: true }),
    layout: "none",
  },
  {
    path: ROUTES.ONBOARDING_BILLING,
    component: BillingOnboarding,
    layout: "none",
    seo: { title: "Billing Onboarding", noindex: true },
  },
  {
    path: ROUTES.STUDENT_ONBOARDING,
    component: StudentOnboarding,
    layout: "none",
    seo: { title: "Student Onboarding", noindex: true },
  },
  {
    path: ROUTES.STUDENT_ONBOARDING_COMPLETE,
    component: StudentOnboarding,
    layout: "none",
    seo: { title: "Student Onboarding Complete", noindex: true },
  },

  // ============================================================================
  // COMPANY MANAGEMENT (no layout)
  // ============================================================================

  {
    path: ROUTES.COMPANY_SELECTOR,
    component: CompanySelectorPage,
    layout: "none",
    seo: { title: "Company Selector", noindex: true },
  },

  // ============================================================================
  // PUBLIC WEBSITE ROUTES (public layout)
  // ============================================================================

  {
    path: ROUTES.LANDING,
    component: Landing,
    layout: "public",
    seo: { title: "RollOps - Gym Management Made Simple" },
  },
  {
    path: ROUTES.PRIVACY,
    component: PrivacyPolicy,
    layout: "public",
    seo: { title: "Privacy Policy" },
  },
  {
    path: ROUTES.TERMS,
    component: Terms,
    layout: "public",
    seo: { title: "Terms of Service" },
  },
  {
    path: ROUTES.SECURITY_POLICY,
    component: SecurityPolicy,
    layout: "public",
    seo: { title: "Vulnerability Disclosure Policy" },
  },
  {
    path: ROUTES.FEATURES_MEMBER_MANAGEMENT,
    component: MemberManagement,
    layout: "public",
    seo: { title: "Member Management - RollOps" },
  },
  {
    path: ROUTES.FEATURES_BILLING,
    component: BillingFeature,
    layout: "public",
    seo: { title: "Billing Features - RollOps" },
  },
  {
    path: ROUTES.FEATURES_SCHEDULING,
    component: SchedulingFeature,
    layout: "public",
    seo: { title: "Scheduling Features - RollOps" },
  },
  {
    path: ROUTES.FOUNDERS_PROMO,
    component: FoundersPromo,
    layout: "public",
    seo: { title: "Founders Promo - RollOps" },
  },
  {
    path: ROUTES.SMS_CONSENT_LEGAL,
    component: SmsConsentLegal,
    layout: "public",
    seo: { title: "SMS Consent Legal", noindex: true },
  },
  {
    path: ROUTES.DELETE_ACCOUNT,
    component: DeleteAccountPage,
    layout: "public",
    seo: { title: "Delete Account", noindex: true },
  },
  {
    path: ROUTES.GET_INFO,
    component: GetInfo,
    layout: "public",
    seo: { title: "Get Info", noindex: true },
  },
  {
    path: ROUTES.SIGNIN,
    component: Signin,
    layout: "none",
    seo: { title: "Sign In", noindex: true },
  },
  {
    path: ROUTES.SIGNUP,
    component: Signup,
    layout: "none",
    seo: { title: "Sign Up", noindex: true },
  },
  {
    path: ROUTES.GUIDED_STUDENT_ONBOARDING,
    component: GuidedStudentOnboarding,
    layout: "none",
    seo: { title: "Guided Student Onboarding", noindex: true },
  },
];
